import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/home';
import { categoryData, allCategoryItems } from '@/mocks/category';

function formatViews(n: number): string {
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
  return String(n);
}

export default function CategoryContent() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'anime');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      window.scrollTo(0, 0);
    }
  }, [slug]);

  const filteredItems = useMemo(() => {
    if (activeSlug === 'all') return allCategoryItems;
    return (categoryData as Record<string, typeof allCategoryItems>)[activeSlug] || [];
  }, [activeSlug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug);

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: activeCategory?.name || '分类' },
  ];

  return (
    <>
      <div className="mx-auto max-w-[1400px] px-4 md:px-6 pt-6">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <section className="bg-background-100 border-b border-background-200">
        <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-6">
          <div className="flex flex-wrap items-center gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 text-foreground-600 hover:bg-background-200 border border-background-200 hover:border-background-300'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-10 md:py-14">
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              {activeCategory?.name || '全部分类'}
            </h1>
            <p className="mt-2 text-sm text-foreground-500">
              共 {filteredItems.length} 部作品
            </p>
          </div>

          {filteredItems.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无内容</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {filteredItems.map((item) => (
                <Link
                  key={item.id}
                  to={`/article/${item.id}`}
                  className="group bg-background-50 border border-background-200 hover:border-primary-200 rounded-lg overflow-hidden transition-all cursor-pointer"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={item.cover}
                      alt={item.title}
                      title={`${item.title} - 凪光在线观看`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    {item.rating > 0 && (
                      <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-black/50 backdrop-blur-sm text-white text-xs">
                        <i className="ri-star-fill text-accent-400 text-xs"></i>
                        {item.rating}
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {item.title}
                    </h3>
                    <p className="mt-2 text-sm text-foreground-500 line-clamp-2 leading-relaxed">
                      {item.description}
                    </p>
                    <div className="mt-3 flex items-center gap-4 text-xs text-foreground-400">
                      {item.episodes > 1 && (
                        <span className="flex items-center gap-1">
                          <i className="ri-play-list-2-line"></i>
                          {item.episodes}话
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {formatViews(item.views)}次播放
                      </span>
                      <span className={`whitespace-nowrap ml-auto px-2 py-0.5 rounded text-xs ${
                        item.status.includes('完结') || item.status.includes('上映') || item.status.includes('发售') || item.status.includes('发布')
                          ? 'bg-secondary-100 text-secondary-700'
                          : 'bg-primary-100 text-primary-700'
                      }`}>
                        {item.status}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}